import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';
import { useAnimatedValue } from '../../hooks/useAnimatedValue';
import Sparkline from '../Sparkline';

const WeatherDataBox = ({ 
    title, 
    value, 
    unit, 
    icon: Icon, 
    trend, 
    trendValue,
    trendLabel,
    color = 'blue',
    min,
    max,
    minTime,
    maxTime,
    subValue,
    bottomInfo,
    loading = false,
    sparklineData = null,
    dataField = null
, sparklineMinSpan = 0 }) => {
    const { t } = useTranslation();
    const isValueAnimating = useAnimatedValue(value);
    
    // Get color classes based on the color prop
    const getColorClasses = () => {
        const colors = {
            blue: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
            green: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
            orange: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
            red: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
            purple: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
            gray: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30'
        };
        return colors[color] || colors.blue;
    };
    
    // Get trend icon. 0 (and parseable strings like "2.9") are shown — the
    // API sometimes returns string values, so we coerce via Number(trend).
    const getTrendIcon = () => {
        if (trend === null || trend === undefined) return null;
        const n = Number(trend);
        if (Number.isNaN(n)) return null;
        if (n > 0) return <FiTrendingUp className="w-4 h-4 text-green-500" />;
        if (n < 0) return <FiTrendingDown className="w-4 h-4 text-red-500" />;
        return <FiMinus className="w-4 h-4 text-gray-500" />;
    };
    
    // If the sparkline is a numeric array and every recent reading is
    // null/undefined/0, the station likely has no sensor for this metric
    // (API returns 0 rather than null for missing fields). Hide the whole
    // card so it doesn't waste grid space with placeholder values.
    // Object-shaped sparklines (e.g. WindBoxV2 passes {speed, gust}) are
    // exempt — `Array.isArray` + numeric `every` check skips them.
    const hasNoSignal = Array.isArray(sparklineData)
        && sparklineData.length > 0
        && sparklineData.every(v => v == null || (typeof v === 'number' && v === 0));
    if (hasNoSignal) return null;

    if (loading) {
        return (
            <div className="w-full md:w-1/2 lg:w-1/3 my-2">
                <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-3 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl">
                    <div className="animate-pulse">
                        <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-24 mb-3 mx-auto md:mx-0"></div>
                        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-16 mb-2 mx-auto md:mx-0"></div>
                        <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-20 mx-auto md:mx-0"></div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="w-full md:w-1/2 lg:w-1/3 my-2">
            <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-4 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl hover:shadow-2xl transition-shadow duration-200 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex flex-col">
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            {t(title)}
                        </h3>
                        {sparklineData && sparklineData.length > 1 && (
                            <div className="mt-1 opacity-60">
                                <Sparkline 
                                    data={sparklineData} 
                                    width={60} 
                                    height={16} 
                                    minSpan={sparklineMinSpan}
                                    color={color === 'blue' ? '#3B82F6' : color === 'green' ? '#10B981' : color === 'orange' ? '#F59E0B' : color === 'red' ? '#EF4444' : color === 'purple' ? '#8B5CF6' : '#6B7280'}
                                    strokeWidth={1.5}
                                />
                            </div>
                        )}
                    </div>
                    <div className={`p-2 rounded-lg ${getColorClasses()}`}>
                        {Icon && <Icon className="w-5 h-5" />}
                    </div>
                </div>
                
                {/* Value */}
                <div className="mb-2 flex justify-center md:justify-start">
                    {typeof value === 'object' ? (
                        value
                    ) : (
                        <div className="flex items-baseline space-x-2">
                            <span className={`text-3xl font-bold text-gray-900 dark:text-white transition-all duration-300 tabular-nums ${isValueAnimating ? 'scale-110 text-blue-600 dark:text-blue-400' : ''}`}>
                                {value !== null && value !== undefined ? Math.round(value) : '--'}
                            </span>
                            <span className="text-lg text-gray-600 dark:text-gray-400">
                                {unit}
                            </span>
                        </div>
                    )}
                </div>
                
                {/* Trend indicator — render whenever trend has any value
                    (number or numeric string, including 0). Coerce via
                    Number() because the API sometimes returns strings. */}
                {trend !== null && trend !== undefined && !Number.isNaN(Number(trend)) && (
                    <div className="mb-3 flex items-center justify-center md:justify-start space-x-1">
                        {getTrendIcon()}
                        {trendValue !== null && trendValue !== undefined && !Number.isNaN(Number(trendValue)) && (
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                {Math.abs(Number(trendValue)).toFixed(1)}{unit}
                                <span className="text-xs opacity-70 ml-1">(1h)</span>
                            </span>
                        )}
                    </div>
                )}
                
                {/* Sub value (like trend info) */}
                {subValue && (
                    <div className="mb-3 text-sm text-gray-600 dark:text-gray-400 text-center md:text-left">
                        {subValue}
                    </div>
                )}
                
                {/* Spacer to push bottom content down */}
                <div className="flex-1"></div>
                
                {/* Min/Max with times or additional info */}
                {(min !== undefined || max !== undefined || bottomInfo) && (
                    <div className="pt-2 border-t border-gray-100 dark:border-slate-700 mt-auto">
                        {bottomInfo ? (
                            bottomInfo
                        ) : (
                            <div className="flex items-center justify-around md:justify-between text-xs text-gray-500 dark:text-gray-400">
                                {min !== undefined && (
                                    <div className="flex flex-col items-center">
                                        <div className="flex items-center space-x-1">
                                            <FiTrendingDown className="w-3 h-3" />
                                            <span>{t('Min')}: {Math.round(min)}{unit}</span>
                                        </div>
                                        {minTime && (
                                            <span className="text-xs opacity-70">{minTime}</span>
                                        )}
                                    </div>
                                )}
                                {max !== undefined && (
                                    <div className="flex flex-col items-center">
                                        <div className="flex items-center space-x-1">
                                            <FiTrendingUp className="w-3 h-3" />
                                            <span>{t('Max')}: {Math.round(max)}{unit}</span>
                                        </div>
                                        {maxTime && (
                                            <span className="text-xs opacity-70">{maxTime}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeatherDataBox;